import React, { useEffect, useState, useRef } from "react";
import "./CollectionPayments.css";
import { logLoadTrace } from "../../../../firebase";
import CustomDataGrid from "../../../../components/dms_common/CustomDataGrid";
import Header from "../../../../components/dms_common/Header";
import FormHeader from "../../../../components/dms_common/FormHeader";
import { useLocation, useNavigate } from "react-router-dom";
import CustomAccordian from "../../../../components/dms_common/CustomAccordian";
import { OutletCollectionDetails } from "../../../../types/types";
import Search from "../../../../components/dms_common/Search";
import {
  PaymentCollectionRow,
  collectionMetaData1,
  collectionMetaData2,
  collectionStatusMapping,
  defaultOutletCollectionDetails,
  paymentApprovalStatusConfig,
  paymentTableColumnsMetaData,
} from "../../collectionUtils";
import { Loader } from "../../../../components/loader/Loader";
import { Button } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import PaymentCard from "../../components/paymentCard/PaymentCard";
import CreditNoteModal from "../../components/creditNoteModal/CreditNoteModal";
import dayjs, { Dayjs } from "dayjs";
import CustomDatePicker from "../../components/customDatePicker/CustomDatePicker";
import {
  fetchAndTransformPaymentCollectionData,
  approvePaymentCollection,
  rejectPaymentCollection,
  fetchAllData,
} from "../../services/collectionService";
import { getUTCDate } from "../../../../services/manageOrderService";
import { createScrollHandler, handleScroll, truncateToTwoDecimals } from "../../../../components/dms_common/DmsUtils";

const CollectionPayments = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Retrieve startDate and endDate from location.state, default to today if not present
const [startDate, setStartDate] = useState<Dayjs>(
  location.state?.startDate ? dayjs(location.state.startDate) : dayjs().subtract(6, 'day')
);
const [endDate, setEndDate] = useState<Dayjs>(
  location.state?.endDate ? dayjs(location.state.endDate) : dayjs()
);

  const [searchText, setSearchText] = useState("");
  const outletCode: string = location.state?.outletCode || "";
  const [isScrolling, setIsScrolling] = useState(false);
  const [loader, setLoader] = useState(true);
  const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false);
  const [paymentHistoryVisible, setPaymentHistoryVisible] = useState(true);
  const [isCreditNoteModalOpen, setIsCreditNoteModalOpen] = useState(false);
  const [rows, setRows] = useState<PaymentCollectionRow[]>([]);
  const paymentHistoryRef = useRef<HTMLDivElement>(null);
  const [creditNoteNumber, setCreditNoteNumber] = useState<string>("");
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<any[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [outletDetails, setOutletDetails] = useState<OutletCollectionDetails>(
    defaultOutletCollectionDetails
  );

  const [scrollTop, setScrollTop] = useState<number>(0); 
  const scrollRef = useRef<HTMLDivElement>(null);
  const [allowTableScroll, setAllowTableScroll] = useState<boolean>(false);
  const [dontAllowContainerScroll, setDontContainerScroll] = useState(true);
  // Compute the total paid amount from rows
  const totalApprovedPaidAmount = rows.reduce((sum, row) => {
    const paid = parseFloat(row.approvedPaidAmount);
    return sum + (isNaN(paid) ? 0 : paid);
  }, 0);

  const modifiedOutletDetails = {
    ...outletDetails,
    totalApprovedPaidAmount,
    creditDueDate: outletDetails.creditDueDate
      ? dayjs(outletDetails.creditDueDate).format("DD-MM-YYYY")
      : outletDetails.creditDueDate,
  };

  useEffect(() => {
    if (isPaymentHistoryOpen) {
      setPaymentHistoryVisible(true);
    } else {
      const handleTransitionEnd = () => {
        setPaymentHistoryVisible(false);
      };
      const element = paymentHistoryRef.current;
      if (element) {
        element.addEventListener("transitionend", handleTransitionEnd);
        return () => {
          element?.removeEventListener("transitionend", handleTransitionEnd);
        };
      }
    }
  }, [isPaymentHistoryOpen]);

  // Synchronize selectedPaymentDetails with updated rows
  useEffect(() => {
    if (selectedRowId !== null) {
      const selectedRow = rows.find((row) => row.id === selectedRowId);
      if (selectedRow) {
        setSelectedPaymentDetails(selectedRow.paymentDetails || []);
      }
    }
  }, [rows, selectedRowId]);

  const fetchAndSaveOuterData = async () => {
    const res = await fetchAllData(outletCode);
    if (Array.isArray(res) && res.length > 0) {
      setOutletDetails(res[0]);
    }
  };

  const fetchData = async (fromDate?: Dayjs, toDate?: Dayjs) => {
    setLoader(true);
    await fetchAndSaveOuterData();
    const loginId = JSON.parse(localStorage.authContext).user.loginId;
    const effectiveStartDate = fromDate || startDate;
    const effectiveEndDate = toDate || endDate;
    try {
      const tableRows: PaymentCollectionRow[] =
        await fetchAndTransformPaymentCollectionData(
          outletCode,
          loginId,
          effectiveStartDate ? getUTCDate(effectiveStartDate, "start") : "",
          effectiveEndDate ? getUTCDate(effectiveEndDate, "end") : ""
        );
      setRows(tableRows || []);
    } catch (error) {
      console.error("Error in fetchData:", error);
      setRows([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    logLoadTrace(fetchData, "collection_outlet_details_page_load_time");
  }, []);

  const onCustomAccordionToggle = (isExpanded: boolean) => {
    setIsScrolling(!isExpanded);
  };

  const handleApply = async () => {
    await fetchData();
  };

  const handleApplyLog = async () => {
    logLoadTrace(handleApply, "manage_secondary_order_page_load_time");
  };

  const handleClearAll = async () => {
    const today = dayjs();
    setStartDate(today);
    setEndDate(today);
    await fetchData(today, today);
  };

  const handleClearAllLog = async () => {
    logLoadTrace(handleClearAll, "manage_secondary_order_page_load_time");
  };

  const handleClickCreditNote = (creditNoteNumber: string) => {
    setCreditNoteNumber(creditNoteNumber);
    setIsCreditNoteModalOpen(true);
  };

  const handleApprove = async (
    index: number,
    shortageValue: string,
    paymentMethod: string,
    idList: string[]
  ) => {
    try {
      const shortage = parseFloat(shortageValue) || 0;
      await approvePaymentCollection(shortage, paymentMethod, idList, modifiedOutletDetails?.outletType?.toLocaleLowerCase());
      await fetchData();
    } catch (error) {
      console.error("Error in handleApprove:", error);
    }
  };

  const handleReject = async (paymentMethod: string, idList: string[]) => {
    try {
      await rejectPaymentCollection(paymentMethod, idList, modifiedOutletDetails?.outletType?.toLocaleLowerCase());
      await fetchData();
    } catch (error) {
      console.error("Error in handleReject:", error);
    }
  };

  const filteredRows = rows.filter((row: PaymentCollectionRow) => {
    const searchTextLower = searchText.toLowerCase();
    return (
      row.paymentDate.toLowerCase().includes(searchTextLower) ||
      row.paidBy.toLowerCase().includes(searchTextLower) ||
      row.paidAmount.toLowerCase().includes(searchTextLower) ||
      row.invoices.join(",").toLowerCase().includes(searchTextLower) ||
      row.creditNoteUsed.join(",").toLowerCase().includes(searchTextLower) ||
      row.paymentApprovalStatus.toLowerCase().includes(searchTextLower)
    );
  });
 useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
  
    const handler = createScrollHandler(
      scrollRef,
      isScrolling,
      setAllowTableScroll,
      setDontContainerScroll
    );
  
    el.addEventListener("wheel", handler);
  
    return () => {
      el.removeEventListener("wheel", handler);
    };
  }, [isScrolling, dontAllowContainerScroll]);
  
  if(loader) {
    return <Loader />
  }

  return (
    <div className="cash-collection-container">
      <CreditNoteModal
        open={isCreditNoteModalOpen}
        onClose={() => {
          setIsCreditNoteModalOpen(false);
          setCreditNoteNumber("");
        }}
        creditNoteNumber={creditNoteNumber}
      />
      <div className="view_order_header_with_search">
        <div className="view_order_header">
          <FormHeader
            parentLabel={"Collection"}
            label={"Outlet Details"}
            goBackToParent={() => navigate("/dashboard/collection")}
          />
        </div>
        <div className="view_order_manage_order_search_bar">
          <Search
            searchFunction={(value) => setSearchText(value)}
            value={searchText}
          />
        </div>
      </div>
      <div className="main-content-container">
        <div
          className={`left-content-container ${
            isPaymentHistoryOpen ? "payment-history-open" : "payment-history-closed"
          }`}
          onWheel={(event) => {
            if (rows?.length > 4){
              handleScroll(event, setIsScrolling, scrollTop)
            }
          }}
        >
        <div ref={scrollRef} style={{
          gap: "14px",
          overflow: (dontAllowContainerScroll) ? "hidden" : "scroll",
          height: "100%",
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE 10+
        }}
        >
          <CustomAccordian
            expanded={!isScrolling}
            onCustomAccordionToggle={onCustomAccordionToggle}
            isSecondHalf={true}
            metaData1={collectionMetaData1}
            data1={outletDetails}
            metaData2={collectionMetaData2}
            data2={modifiedOutletDetails}
            header={`Outlet Code ${modifiedOutletDetails?.outletCode}`}
            label1={""}
            status={outletDetails?.status}
            statusConfig={collectionStatusMapping()}
            singleColumnInEachHalf={true}
          />
          <div style={{height: "100%", position: "sticky", top: "0"}}>
          <div className="view_order_details_vo">
            <div className="invoice-header-container">
              <Header title={"Collection details"} />
            </div>

            <div className="controls-container">
              <div className="manage_order_support_utility_dates_manageOrder">
                <div className="date-picker-container">
                  <CustomDatePicker
                    label="From Date"
                    value={startDate}
                    onChange={(value) => {
                      if (value) {
                        setStartDate(value);
                      }
                    }}
                    disabled={false}
                    className="manage_order_dateEnter"
                  />
                </div>
                <div className="date-picker-container">
                  <CustomDatePicker
                    label="To Date"
                    value={endDate}
                    onChange={(value) => {
                      if (value) {
                        setEndDate(value);
                      }
                    }}
                    disabled={false}
                    className="manage_order_dateEnter"
                  />
                </div>
                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#237C9D",
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                      backgroundColor: "#237C9D",
                    },
                  }}
                  onClick={handleApplyLog}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#f0fbff",
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                      backgroundColor: "#f0fbff",
                    },
                  }}
                  onClick={handleClearAllLog}
                >
                  Reset
                </Button>
              </div>
              {outletDetails.creditStatus?.toLocaleUpperCase() !== "PAID" && (
                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    minWidth: "130px",
                    backgroundColor: "var(--dashboard-theme-color)",
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                      background: "var(--dashboard-theme-color)",
                    },
                  }}
                  onClick={() =>
                    navigate(
                      `recordPayment?type=${modifiedOutletDetails?.outletType?.toLocaleLowerCase()}`,
                      {
                        state: {
                          outletDetails,
                          startDate: startDate.toISOString(), // Pass startDate
                          endDate: endDate.toISOString(), // Pass endDate
                        },
                      }
                    )
                  }
                >
                  Cash Collection
                </Button>
              )}
            </div>
              <CustomDataGrid
                rows={Array.isArray(filteredRows) ? filteredRows : []}
                columnsMetaData={paymentTableColumnsMetaData}
                orderStatusConfig={paymentApprovalStatusConfig()}
                onEditClick={(row) => {
                  setSelectedRowId(row.id);
                  setSelectedPaymentDetails(row.paymentDetails || []);
                  setIsPaymentHistoryOpen((prev) => !prev);
                }}
                handleClickCreditNote={handleClickCreditNote}
              />
            </div>
          </div>
          </div>
        </div>
        {paymentHistoryVisible && (
          <div
            ref={paymentHistoryRef}
            className={`payment-history ${
              isPaymentHistoryOpen ? "open" : "closed"
            }`}
          >
            <div className="payment-history-header">
              <span className="payment-history-title">Payment History</span>
              <ClearIcon
                sx={{ color: "#2525258f", cursor: "pointer" }}
                onClick={() => {
                  setIsPaymentHistoryOpen(false);
                  setSelectedRowId(null);
                }}
              />
            </div>
            <div className="cashCollection_paymentCardContainer">
              {selectedPaymentDetails.length > 0 ? (
                selectedPaymentDetails.map((payment, index) => (
                  <PaymentCard
                    key={index}
                    paymentDate={payment.paymentDate}
                    paymentMode={payment.paymentMethod}
                    paidAmount={`₹ ${truncateToTwoDecimals(payment.paidAmount)}`}
                    finalAmount={`₹ ${truncateToTwoDecimals(
                      payment.paidAmount - payment.shortageAmount
                    )}`}
                    shortageAmount={truncateToTwoDecimals(payment.shortageAmount)}
                    handleApprove={(shortageValue) =>
                      handleApprove(
                        index,
                        shortageValue,
                        payment.paymentMethod,
                        payment.idList
                      )
                    }
                    handleReject={() =>
                      handleReject(payment.paymentMethod, payment.idList)
                    }
                    status={payment.status}
                  />
                ))
              ) : (
                <div>No payment details available.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionPayments;
